import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/context/AuthContext';
import { directoryService } from '@/services/directoryService';

import type { UserDirectoryEntry } from '@/types/directory';

interface UserDirectoryContextValue {
  /** Lookup by display name (most common in DTOs) or domain account. */
  getEntry: (nameOrAccount: string) => UserDirectoryEntry | undefined;
  /** Get photo URL for any identifier (display name or domain account). */
  getPhotoUrl: (nameOrAccount: string) => string | null;
  /** Get email for a display name or domain account. */
  getEmail: (nameOrAccount: string) => string | null;
  /** Whether directory has loaded. */
  isLoaded: boolean;
}

const UserDirectoryContext = createContext<UserDirectoryContextValue | undefined>(undefined);

interface UserDirectoryProviderProps {
  children: React.ReactNode;
}

export const UserDirectoryProvider: React.FC<UserDirectoryProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<UserDirectoryEntry[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    directoryService.getDirectory()
      .then(data => {
        if (!cancelled) {
          setEntries(data);
          setIsLoaded(true);
        }
      })
      .catch((err: unknown) => {
        console.error('==[UserDirectory] Failed to load directory:', err);
        if (!cancelled) setIsLoaded(true);
      });

    return () => { cancelled = true; };
  }, [user]);

  const byDisplayName = useMemo(() => {
    const map = new Map<string, UserDirectoryEntry>();
    for (const entry of entries) {
      map.set(entry.displayName.toLowerCase(), entry);
    }
    return map;
  }, [entries]);

  const byAccount = useMemo(() => {
    const map = new Map<string, UserDirectoryEntry>();
    for (const entry of entries) {
      map.set(entry.domainAccount.toLowerCase(), entry);
    }
    return map;
  }, [entries]);

  const getEntry = useCallback(
    (nameOrAccount: string): UserDirectoryEntry | undefined => {
      const key = nameOrAccount.toLowerCase();
      return byDisplayName.get(key) ?? byAccount.get(key);
    },
    [byDisplayName, byAccount],
  );

  const getPhotoUrl = useCallback(
    (nameOrAccount: string): string | null => {
      const entry = getEntry(nameOrAccount);
      if (!entry?.photoBase64) return null;
      return `data:image/jpeg;base64,${entry.photoBase64}`;
    },
    [getEntry],
  );

  const getEmail = useCallback(
    (nameOrAccount: string): string | null => {
      const entry = getEntry(nameOrAccount);
      return entry?.email ?? null;
    },
    [getEntry],
  );

  const value = useMemo(() => ({
    getEntry,
    getPhotoUrl,
    getEmail,
    isLoaded,
  }), [getEntry, getPhotoUrl, getEmail, isLoaded]);

  return (
    <UserDirectoryContext.Provider value={value}>
      {children}
    </UserDirectoryContext.Provider>
  );
};

export const useUserDirectory = (): UserDirectoryContextValue => {
  const context = useContext(UserDirectoryContext);
  if (!context) throw new Error('useUserDirectory must be used within UserDirectoryProvider');
  return context;
};
